/**
 * Admin → System → Pipelines (#885, D4): the definitions behind the Deal Pipeline (`deal`),
 * the Lead Pipeline (`contact`) and Event Outreach workflows (`event_contact`), their version
 * history and templates. The two singletons open straight into the editor; Event Outreach is a
 * list of pipelines first.
 *
 * Every publish sends `expected_version`; a stale one answers 409 `version_conflict` and the
 * editor keeps its edits until the admin chooses Reload. A deal (or Lead) version that drops a
 * stage still holding deals (or leads) answers 409 `deal_stage_in_use` (`lead_stage_in_use`) and
 * opens a confirm dialog; confirming re-sends with `?force=true`. Every server error is rendered
 * through `errorText`.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { invalidatePipelineDefinition } from '../../hooks/usePipelineDefinition';
import { errorText } from '../../lib/apiErrors';
import { plural } from '../../lib/plural';
import { createPipelineDefinitionsApi } from '../../lib/pipelineDefinitionsApi';
import PipelineStageEditor, { draftFromDefinition, draftProblem, serializeDraft } from './PipelineStageEditor';
import {
    ApplyTemplateDialog, EventPipelineList, PIPELINE_LABEL, RenamePipelineDialog, SaveTemplateDialog, VersionHistoryDialog,
    pillCls,
} from './PipelineDefinitionParts';

const KINDS = ['deal', 'contact', 'event_contact'].map((id) => ({ id, label: PIPELINE_LABEL[id] }));

// How event-contact rows use these definitions (#887 P3): a row pins the default pipeline's head version at
// its first stage write, and the "Event Outreach" baseline (`event-outreach`) is created by that first
// write, never by a read. Choosing a pipeline per event is #886's.
const EVENT_CONTACT_BANNER = 'An event contact is pinned to the current version of the default Event Outreach pipeline '
    + 'the first time it is given a stage. If there is none yet, a baseline "Event Outreach" pipeline is created '
    + 'automatically. Editing publishes a new version and leaves contacts already pinned on theirs. '
    + 'Choosing a pipeline for each event is coming in a later release.';

export default function PipelineDefinitionsTab({ initialKind = 'deal', initialHistory = false }) {
    const { getAuthHeader } = useAuth();
    // `api` must keep its identity for the life of the tab: the load effects depend on it, and a token
    // refresh (a new getAuthHeader) would otherwise reload the definition and discard the admin's draft.
    const authRef = useRef(getAuthHeader);
    useEffect(() => { authRef.current = getAuthHeader; });
    const api = useMemo(() => createPipelineDefinitionsApi(() => authRef.current()), []);

    const [kind, setKind] = useState(KINDS.some((k) => k.id === initialKind) ? initialKind : 'deal');
    const [reloadKey, setReloadKey] = useState(0);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [definitions, setDefinitions] = useState([]);
    const [editingId, setEditingId] = useState(null);
    const [draft, setDraft] = useState(null);
    const [baseline, setBaseline] = useState('');
    const [templates, setTemplates] = useState(null); // null: not loaded, or the admin-only list was refused
    const [templatesNote, setTemplatesNote] = useState(null); // why the template actions are missing, when the list failed
    const [renaming, setRenaming] = useState(null); // the Event Outreach pipeline being renamed
    const [busy, setBusy] = useState(false);
    const [publishError, setPublishError] = useState(null);
    const [conflict, setConflict] = useState(null); // { currentVersion }
    const [forceConfirm, setForceConfirm] = useState(null); // { stagesInUse, retry }
    const [dialog, setDialog] = useState(null); // 'history' | 'apply' | 'save'
    const reopenRef = useRef(null);
    const historyOnLoadRef = useRef(initialHistory); // `?history=1`: open the history once, after the first load

    const openEditor = useCallback((definition) => {
        const next = draftFromDefinition(definition);
        setEditingId(definition.pipeline_id);
        setDraft(next);
        setBaseline(JSON.stringify(serializeDraft(definition.record_kind, next)));
        setPublishError(null);
        setConflict(null);
    }, []);
    const closeEditor = useCallback(() => {
        setEditingId(null);
        setDraft(null);
        setBaseline('');
        setPublishError(null);
        setConflict(null);
    }, []);
    const reload = (openId = null) => {
        reopenRef.current = openId;
        setReloadKey((n) => n + 1);
    };

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setLoadError(null);
        api.list(kind).then((rows) => {
            if (cancelled) return;
            setDefinitions(rows);
            const open = kind === 'event_contact' ? rows.find((r) => r.pipeline_id === reopenRef.current) : rows[0];
            reopenRef.current = null;
            if (open) openEditor(open); else closeEditor();
            if (open && kind !== 'event_contact' && historyOnLoadRef.current) setDialog('history');
            historyOnLoadRef.current = false;
        }).catch((err) => {
            if (!cancelled) setLoadError(errorText(err, 'Failed to load pipelines'));
        }).finally(() => {
            if (!cancelled) setLoading(false);
        });
        return () => { cancelled = true; };
    }, [api, kind, reloadKey, openEditor, closeEditor]);

    useEffect(() => {
        let cancelled = false;
        setTemplates(null);
        setTemplatesNote(null);
        api.templates(kind).then((rows) => {
            if (!cancelled) setTemplates(rows);
        }).catch((err) => {
            if (cancelled) return;
            // 403: the list is admin-only (and needs MFA), so the template actions are not offered,
            // and a line says why instead of leaving the buttons to vanish unexplained
            if (err?.response?.status !== 403) toast.error(errorText(err, 'Failed to load templates'));
            setTemplatesNote(errorText(err, 'Templates could not be loaded'));
        });
        return () => { cancelled = true; };
    }, [api, kind]);

    const definition = definitions.find((d) => d.pipeline_id === editingId) || null;
    const editing = !!(definition && draft);
    const dirty = editing && JSON.stringify(serializeDraft(kind, draft)) !== baseline;

    const selectKind = (next) => {
        if (next === kind) return;
        if (dirty && !window.confirm('Discard your unsaved changes?')) return;
        closeEditor();
        setDefinitions([]);
        setLoading(true);
        setKind(next);
    };
    const leaveEditor = () => {
        if (dirty && !window.confirm('Discard your unsaved changes?')) return;
        closeEditor();
    };

    /** Run one write that publishes a version, mapping the two 409s the server answers with. */
    const attempt = async (action, { force = false, done } = {}) => {
        setBusy(true);
        setPublishError(null);
        setConflict(null);
        try {
            done(await action(force));
        } catch (err) {
            const detail = err?.response?.data?.detail;
            if (err?.response?.status === 409 && detail?.code === 'version_conflict') {
                setConflict({ currentVersion: detail.current_version });
            } else if (err?.response?.status === 409 && (detail?.code === 'deal_stage_in_use' || detail?.code === 'lead_stage_in_use')) {
                setForceConfirm({
                    kind: detail.code === 'lead_stage_in_use' ? 'lead' : 'deal',
                    stagesInUse: detail.stages_in_use || [],
                    retry: () => attempt(action, { force: true, done }),
                });
            } else {
                setPublishError(errorText(err, 'Failed to save'));
            }
        } finally {
            setBusy(false);
        }
    };

    const onPublished = (published) => {
        invalidatePipelineDefinition(published.pipeline_id);
        setDefinitions((prev) => prev.map((d) => (d.pipeline_id === published.pipeline_id ? published : d)));
        openEditor(published);
        toast.success(`Published v${published.version}`);
    };

    const publish = () => {
        const problem = draftProblem(draft);
        if (problem) { setPublishError(problem); return; }
        const body = { expected_version: definition.version, ...serializeDraft(kind, draft) };
        attempt((force) => api.publish(definition.pipeline_id, body, { force }), { done: onPublished });
    };

    const restore = (version) => {
        setDialog(null);
        attempt((force) => api.restore(definition.pipeline_id, version, definition.version, { force }), { done: onPublished });
    };

    // A new Event Outreach pipeline: a refusal (a taken name, the 20-pipeline limit) is a toast and the
    // dialog stays open with what was typed, as for rename and save-as-template.
    const createFromTemplate = async (template, name) => {
        setBusy(true);
        try {
            const created = await api.applyTemplate(template.template_id, { name });
            setDialog(null);
            toast.success(`Created ${created.name}`);
            reload(created.pipeline_id);
        } catch (err) {
            toast.error(errorText(err, 'Failed to create the pipeline'));
        } finally {
            setBusy(false);
        }
    };

    const applyTemplate = (template, name) => {
        if (kind === 'event_contact') {
            createFromTemplate(template, name);
            return;
        }
        setDialog(null);
        attempt((force) => api.applyTemplate(template.template_id, { expected_version: definition.version }, { force }),
                { done: onPublished });
    };

    const saveAsTemplate = async (name) => {
        setBusy(true);
        try {
            const created = await api.saveAsTemplate({ from_pipeline_id: definition.pipeline_id, name });
            setTemplates((prev) => [...(prev || []), created]);
            setDialog(null);
            toast.success('Template saved');
        } catch (err) {
            toast.error(errorText(err, 'Failed to save the template'));
        } finally {
            setBusy(false);
        }
    };

    const renameTemplate = async (template, name) => {
        setBusy(true);
        try {
            const updated = await api.updateTemplate(template.template_id, { name });
            setTemplates((prev) => prev.map((t) => (t.template_id === updated.template_id ? updated : t)));
            toast.success('Template renamed');
            return true;
        } catch (err) {
            toast.error(errorText(err, 'Failed to rename the template'));
            return false;
        } finally {
            setBusy(false);
        }
    };

    const deleteTemplate = async (template) => {
        setBusy(true);
        try {
            await api.deleteTemplate(template.template_id);
            setTemplates((prev) => prev.filter((t) => t.template_id !== template.template_id));
            toast.success('Template deleted');
            return true;
        } catch (err) {
            toast.error(errorText(err, 'Failed to delete the template'));
            return false;
        } finally {
            setBusy(false);
        }
    };

    const runRowAction = async (action, success) => {
        setBusy(true);
        try {
            await action();
            toast.success(success);
            reload();
        } catch (err) {
            toast.error(errorText(err, 'That did not work'));
        } finally {
            setBusy(false);
        }
    };

    // The dialog stays open when the rename is refused (a 409/422 message is on screen), so the typed name is not lost.
    const renamePipeline = async (pipeline, name) => {
        setBusy(true);
        try {
            await api.rename(pipeline.pipeline_id, { name });
            toast.success('Pipeline renamed');
            setRenaming(null);
            reload();
        } catch (err) {
            toast.error(errorText(err, 'Failed to rename the pipeline'));
        } finally {
            setBusy(false);
        }
    };

    const confirmForce = () => {
        const { retry } = forceConfirm;
        setForceConfirm(null);
        retry();
    };

    const conflictBanner = conflict && (
        <div role="alert" className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
             data-testid="pipeline-version-conflict">
            <span>
                {conflict.currentVersion != null
                    ? `Someone published v${conflict.currentVersion} while you were editing. Reload to review it.`
                    : 'Someone published a newer version while you were editing. Reload to review it.'}
            </span>
            <Button size="sm" variant="outline" data-testid="pipeline-reload" onClick={() => reload(definition?.pipeline_id)}>Reload</Button>
        </div>
    );
    const errorBanner = publishError && (
        <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="pipeline-publish-error">
            {publishError}
        </div>
    );

    // #986: the deal and lead pipelines show their fixed name; only Event Outreach shows the stored, editable one.
    const displayName = kind === 'event_contact' ? definition?.name : PIPELINE_LABEL[kind];
    const header = definition && (
        <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2">
                {kind === 'event_contact' && (
                    <button type="button" className="text-sm text-stone-500 hover:text-stone-800" data-testid="pipeline-back" onClick={leaveEditor}>
                        ← All pipelines
                    </button>
                )}
                <h3 className="text-base font-semibold text-stone-800" data-testid="pipeline-name">{displayName}</h3>
                <span className={pillCls} data-testid="pipeline-version">{definition.stored ? `v${definition.version}` : 'Not published yet'}</span>
                <span className={pillCls}>{definition.active ? 'Active' : 'Inactive'}</span>
                {definition.is_default && <span className={pillCls}>Default</span>}
                {kind === 'event_contact' && Number(definition.schema) === 2 && (
                    <span className={pillCls} data-testid="pipeline-schema"
                          title="Six fixed columns you can rename, plus the follow-up stages you add">Event journey</span>
                )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
                {kind !== 'event_contact' && templates && (
                    <Button variant="outline" size="sm" disabled={busy} data-testid="apply-template" onClick={() => setDialog('apply')}>Apply template</Button>
                )}
                <Button variant="outline" size="sm" data-testid="version-history" onClick={() => setDialog('history')}>Version history</Button>
                {templates && (
                    <Button variant="outline" size="sm" disabled={busy || dirty} data-testid="save-as-template"
                            title={dirty ? 'Publish or discard your edits first: a template saves the published version' : undefined}
                            onClick={() => setDialog('save')}>
                        Save as template
                    </Button>
                )}
            </div>
        </div>
    );

    return (
        <div className="space-y-5" data-testid="pipeline-definitions-tab">
            <div className="flex items-center gap-1 bg-stone-100 rounded-lg p-1 w-fit">
                {KINDS.map((k) => (
                    <button key={k.id} type="button" data-testid={`pipeline-kind-${k.id}`} aria-pressed={kind === k.id}
                            onClick={() => selectKind(k.id)}
                            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${kind === k.id ? 'bg-stone-900 text-white' : 'text-stone-600 hover:text-stone-900'}`}>
                        {k.label}
                    </button>
                ))}
            </div>

            {kind === 'event_contact' && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="event-contact-banner">
                    {EVENT_CONTACT_BANNER}
                </div>
            )}

            {loading ? (
                <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 text-stone-400 animate-spin" /></div>
            ) : loadError ? (
                <div className="text-center py-10" data-testid="pipeline-load-error">
                    <p className="text-sm text-red-600">{loadError}</p>
                    <Button variant="outline" size="sm" className="mt-3" onClick={() => reload()}>Retry</Button>
                </div>
            ) : kind === 'event_contact' && !editing ? (
                <>
                    {errorBanner}
                    <EventPipelineList pipelines={definitions} canCreate={!!templates} templatesNote={templatesNote} busy={busy}
                                   onNew={() => setDialog('apply')}
                                   onEdit={openEditor}
                                   onRename={setRenaming}
                                   onClone={(d) => runRowAction(() => api.clone(d.pipeline_id), 'Pipeline cloned')}
                                   onToggleActive={(d) => runRowAction(
                                       () => (d.active ? api.deactivate(d.pipeline_id) : api.activate(d.pipeline_id)),
                                       d.active ? 'Pipeline deactivated' : 'Pipeline activated')}
                                   onMakeDefault={(d) => runRowAction(() => api.makeDefault(d.pipeline_id), 'Default pipeline changed')} />
                </>
            ) : editing ? (
                <div className="space-y-4">
                    {header}
                    {!templates && templatesNote && (
                        <p className="text-xs text-stone-500" data-testid="templates-unavailable">Templates unavailable: {templatesNote}</p>
                    )}
                    {!definition.stored && (
                        <p className="text-sm text-stone-500" data-testid="pipeline-unpublished">
                            Your organization still runs the built-in {PIPELINE_LABEL[kind]} stages. Publishing records them as v1 and your change as v2.
                        </p>
                    )}
                    <PipelineStageEditor kind={kind} draft={draft} onChange={setDraft} disabled={busy} />
                    {conflictBanner}
                    {errorBanner}
                    <div className="flex justify-end gap-2">
                        <Button variant="outline" disabled={busy || !dirty} data-testid="pipeline-discard" onClick={() => openEditor(definition)}>Discard</Button>
                        <Button disabled={busy || !dirty} data-testid="pipeline-publish" onClick={publish}>
                            {busy ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Publishing…</>
                                : definition.stored ? `Publish v${definition.version + 1}` : 'Publish'}
                        </Button>
                    </div>
                </div>
            ) : null}

            {dialog === 'history' && editing && (
                <VersionHistoryDialog api={api} definition={definition} dirty={dirty} onRestore={restore} onClose={() => setDialog(null)} />
            )}
            {dialog === 'apply' && templates && (
                <ApplyTemplateDialog kind={kind} templates={templates} definition={editing ? definition : null} dirty={!!dirty} busy={busy}
                                     onApply={applyTemplate} onRename={renameTemplate} onDelete={deleteTemplate} onClose={() => setDialog(null)} />
            )}
            {renaming && (
                <RenamePipelineDialog pipeline={renaming} busy={busy} onSave={(name) => renamePipeline(renaming, name)} onClose={() => setRenaming(null)} />
            )}
            {dialog === 'save' && editing && (
                <SaveTemplateDialog busy={busy} defaultName={displayName} onSave={saveAsTemplate} onClose={() => setDialog(null)} />
            )}
            {forceConfirm && (
                <Dialog open onOpenChange={(open) => { if (!open) setForceConfirm(null); }}>
                    <DialogContent className="sm:max-w-md" data-testid={`${forceConfirm.kind}-stage-in-use-dialog`}>
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2 text-amber-600"><AlertTriangle className="w-5 h-5" /> Stages still in use</DialogTitle>
                            <DialogDescription className="sr-only">Some removed stages still have {forceConfirm.kind}s.</DialogDescription>
                        </DialogHeader>
                        <ul className="text-sm text-stone-700 list-disc pl-5 space-y-1">
                            {forceConfirm.stagesInUse.map((s) => {
                                const n = s.deal_count ?? s.lead_count ?? 0;
                                return <li key={s.key}>{plural(n, forceConfirm.kind)} {n === 1 ? 'is' : 'are'} in '{s.key}'.</li>;
                            })}
                        </ul>
                        <p className="text-sm text-stone-600">
                            {forceConfirm.kind === 'lead'
                                ? 'Move them first, or publish anyway: they keep their stage, but they will not appear on the Lead Pipeline board until they are moved.'
                                : "Move them first, or publish anyway and they will show under 'Unmapped stage' until moved."}
                        </p>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setForceConfirm(null)}>Cancel</Button>
                            <Button data-testid="pipeline-force-confirm" onClick={confirmForce}>Publish anyway</Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}
        </div>
    );
}
